import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Paper, TextField, CircularProgress, Alert, Button } from '@mui/material';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase'; // Verify this path is correct for your project structure
import { toast } from 'react-toastify';

const AiProviderSettings = () => {
  const [frequency, setFrequency] = useState('');
  const [initialFrequency, setInitialFrequency] = useState('');
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const settingsRef = doc(db, 'settings', 'platformDefaults');
      const docSnap = await getDoc(settingsRef);

      if (docSnap.exists()) {
        const settingsData = docSnap.data();
        const freqValue = settingsData.globalClaudeOpusFrequency || 5;
        setFrequency(freqValue);
        setInitialFrequency(freqValue);
      } else {
        setFrequency(5);
        setInitialFrequency(5);
        setError('Platform settings document not found. Using default value.');
      }
    } catch (err) {
      console.error("Error fetching AI provider settings:", err);
      setError('Failed to fetch settings. Please try again.');
      setFrequency(5);
      setInitialFrequency(5);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleSave = async () => {
    const numericFrequency = parseInt(frequency, 10);
    if (isNaN(numericFrequency) || numericFrequency < 0) {
      toast.error("Please enter a valid, non-negative number.");
      return;
    }

    setIsSaving(true);
    const toastId = toast.loading("Saving settings...");

    try {
      const settingsRef = doc(db, 'settings', 'platformDefaults');
      await setDoc(settingsRef, {
        globalClaudeOpusFrequency: numericFrequency,
        lastUpdated: serverTimestamp()
      }, { merge: true }); // Use merge: true to avoid overwriting other settings

      setInitialFrequency(numericFrequency); // Update the initial state to the new saved state
      toast.update(toastId, { render: "Settings saved successfully!", type: "success", isLoading: false, autoClose: 4000 });
    } catch (err) {
      console.error("Error saving settings:", err);
      toast.update(toastId, { render: `Failed to save settings: ${err.message}`, type: "error", isLoading: false, autoClose: 5000 });
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <CircularProgress size={24} />
        <Typography>Loading AI Settings...</Typography>
      </Box>
    );
  }
  
  const hasUnsavedChanges = frequency !== initialFrequency;

  return (
    <Paper sx={{ p: 3 }} elevation={2}>
      <Box component="form" noValidate autoComplete="off">
        <Typography variant="h6" gutterBottom>
          AI Provider Routing Settings
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Configure the default AI provider and routing frequency. A value of '3' means every 3rd question will be routed to the alternate provider (Claude Opus). A value of '1' means all questions will be routed.
        </Typography>

        {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}

        <TextField
          fullWidth
          type="number"
          label="Global Claude Opus Frequency"
          id="global-claude-frequency"
          value={frequency}
          onChange={(e) => setFrequency(e.target.value)}
          helperText="Enter a whole number. e.g., 3"
          variant="outlined"
          disabled={isSaving}
        />

        <Button
          variant="contained"
          sx={{ mt: 2 }}
          onClick={handleSave}
          disabled={!hasUnsavedChanges || isSaving || loading}
        >
          {isSaving ? <CircularProgress size={24} /> : "Save Settings"}
        </Button>
      </Box>
    </Paper>
  );
};

export default AiProviderSettings;